import { countWords } from '@/utils/articleWordCount'

/**
 * Body-only word count from print XML: prose `p` and `h1` only.
 * Excludes deck/summary duplicate, captions, credits, image placeholders, and PLS pict blocks.
 *
 * @param {string} xmlString
 * @returns {number}
 */
export function countWordsFromPrintDocumentXml (xmlString) {
  if (!xmlString || typeof xmlString !== 'string') return 0

  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlString, 'text/xml')
  const docbody = doc.getElementsByTagName('body')
  if (!docbody.length) return 0

  let deckText = ''
  try {
    const deck = docbody[0].getElementsByClassName('deck')[0]
    deckText = (deck && deck.childNodes[0] && deck.childNodes[0].nodeValue)
      ? String(deck.childNodes[0].nodeValue).trim()
      : ''
  } catch (e) {
    // no deck
  }

  const chunks = []
  const nodes = doc.querySelectorAll('*')

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i]
    const tag = node.tagName || ''

    if (tag === 'pam:caption' || tag === 'pam:credit' || tag === 'dc:type') {
      continue
    }

    if (node.childNodes.length === 1 && node.childNodes[0].nodeType === 3) {
      if (tag === 'p') {
        const t = (node.textContent || '').trim()
        if (t && t !== deckText) chunks.push(t)
      }
      if (tag === 'h1') {
        const t = (node.textContent || '').trim()
        if (t) chunks.push(t)
      }
    } else if (node.childNodes.length !== 1 && tag === 'p') {
      const t = (node.textContent || '').trim()
      if (t && t !== deckText) chunks.push(t)
    }
  }

  return countWords(chunks.join(' '))
}

/**
 * Build article HTML and metadata from a print document's embedded NewsML-style XML.
 * Pure function — keeps DOM parsing out of Vue components for easier testing and reuse.
 *
 * @param {string} xmlString
 * @returns {{ xmlContent: string, xmlArticleName: string, xmlPublicationName: string, xmlSummary: string, xmlByline: string }}
 */
export function parsePrintDocumentArticleXml (xmlString) {
  const empty = {
    xmlContent: '',
    xmlArticleName: '',
    xmlPublicationName: '',
    xmlSummary: '',
    xmlByline: ''
  }

  if (!xmlString || typeof xmlString !== 'string') {
    return empty
  }

  let xmlByline = ''
  let xmlText = ''
  let xmlSummary = ''
  let xmlArticleName = ''
  let xmlPublicationName = ''

  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlString, 'text/xml')
  const docbody = doc.getElementsByTagName('body')

  try {
    xmlArticleName = doc.getElementsByTagName('dc:title')[0].childNodes[0].nodeValue
    xmlPublicationName = doc.getElementsByTagName('prism:publicationName')[0].childNodes[0].nodeValue
    for (let l = 0; l < doc.getElementsByTagName('dc:creator').length; l++) {
      xmlByline += doc.getElementsByTagName('dc:creator')[l].childNodes[0].nodeValue
      xmlByline += ', '
    }
    for (let ii = 0; ii < doc.getElementsByTagName('dc:contributor').length; ii++) {
      xmlByline += doc.getElementsByTagName('dc:contributor')[ii].childNodes[0].nodeValue
      xmlByline += ', '
    }
    xmlByline = xmlByline.slice(0, -2)
  } catch (e) {
    // preserve partial metadata if any
  }

  try {
    xmlSummary = docbody[0].getElementsByClassName('deck')[0].childNodes[0].nodeValue
  } catch (e) {
    // no deck
  }

  const nodes = doc.querySelectorAll('*')
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i]
    if (node.childNodes.length === 1 && node.childNodes[0].nodeType === 3) {
      if (node.tagName === 'p') {
        if (xmlSummary === node.textContent) {
          xmlText += ''
        } else {
          xmlText += '<br/>' + node.textContent + '<br/>'
        }
      }
      if (node.tagName === 'dc:type') {
        xmlText += '<br/><b>' + '[IMAGES]' + '</b><br/>'
      }
      if (node.tagName === 'pam:caption') {
        xmlText += '<br/><i>' + node.textContent + '</i><br/>'
      }
      if (node.tagName === 'pam:credit') {
        xmlText += '<br/><small>' + node.textContent + '</small><br/>'
      }
      if (node.tagName === 'div') {
        xmlText += '<br/><b>' + '[BOX]' + '</b><br/>'
      }
      if (node.tagName === 'h1') {
        xmlText += '<br/><b>' + node.textContent + '</b><br/>'
      }
    } else if (node.childNodes.length !== 1) {
      if (node.tagName === 'p') {
        xmlText += '<br/>' + node.textContent + '<br/>'
      }
    }
  }

  for (let j = 0; j < docbody.length; j++) {
    try {
      if (docbody[0].getElementsByTagName('TimeInc:PLSpict')[j].nodeName === 'TimeInc:PLSpict') {
        xmlText += '<br/><b>[IMAGES]</b><br/>'
        for (let n = 0; n < docbody[0].getElementsByTagName('TimeInc:PLSpict').length; n++) {
          xmlText += '<br/>' + docbody[0].getElementsByTagName('TimeInc:PLSpict')[n].childNodes[0].nodeValue + '<br/>'
        }
      }
    } catch (e) {
      // skip
    }
  }

  const brIdx = xmlText.indexOf('<br/>')
  const xmlContent = brIdx === -1 ? '' : xmlText.slice(brIdx + 5, xmlText.length)

  return {
    xmlContent,
    xmlArticleName,
    xmlPublicationName,
    xmlSummary,
    xmlByline
  }
}
